<template>
  <Drawer
    v-model:visible="drawerVisible"
    :position="position"
    :class="drawerClass"
    :style="drawerStyle"
    :header="header"
    @hide="$emit('hide')"
  >
    <template #header v-if="showHeader">
      <div class="flex align-items-center gap-3">
        <Avatar v-if="organization?.logo" :image="organization.logo" shape="circle" size="large" />
        <div>
          <h3 class="m-0 text-xl font-semibold">{{ organizationName }}</h3>
        </div>
      </div>
    </template>

    <div v-if="organization" class="space-y-6">
      <!-- Contact Information -->
      <div>
        <h4 class="text-lg font-semibold mb-3">Contact Information</h4>
        <div class="grid">
          <div class="col-12 md:col-6 mb-3">
            <div class="text-500 mb-1">Email</div>
            <div class="text-900 font-medium">{{ organizationEmail || 'N/A' }}</div>
          </div>
          <div class="col-12 md:col-6 mb-3">
            <div class="text-500 mb-1">Phone</div>
            <div class="text-900 font-medium">{{ organizationPhone || 'N/A' }}</div>
          </div>
          <div class="col-12 mb-3">
            <div class="text-500 mb-1">Address</div>
            <div class="text-900 font-medium">{{ organizationAddress || 'N/A' }}</div>
          </div>
          <div class="col-12 mb-3" v-if="organization.website">
            <div class="text-500 mb-1">Website</div>
            <div class="text-900 font-medium">
              <a :href="organization.website" target="_blank" class="text-primary no-underline">
                {{ organization.website }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Business Information -->
      <div v-if="organization.industry || organization.employeeCount">
        <h4 class="text-lg font-semibold mb-3">Business Information</h4>
        <div class="grid">
          <div class="col-12 md:col-6 mb-3" v-if="organization.industry">
            <div class="text-500 mb-1">Industry</div>
            <div class="text-900 font-medium">{{ organization.industry }}</div>
          </div>
          <div class="col-12 md:col-6 mb-3" v-if="organization.employeeCount">
            <div class="text-500 mb-1">Employee Count</div>
            <div class="text-900 font-medium">{{ organization.employeeCount }}</div>
          </div>
        </div>
      </div>

      <!-- Logo -->
      <div v-if="organization?.logo" class="mb-4">
        <h4 class="text-lg font-semibold mb-3">Logo</h4>
        <img
          :src="organization.logo"
          alt="Organization Logo"
          class="border-round shadow-1"
          style="max-height: 200px; object-fit: contain"
        />
      </div>

      <!-- Attachments -->
      <div v-if="organization?.attachments && organization.attachments.length > 0">
        <h4 class="text-lg font-semibold mb-3">Documents</h4>
        <div class="grid">
          <div
            v-for="attachment in organization.attachments"
            :key="attachment.id"
            class="col-12 md:col-6 mb-3"
          >
            <img
              :src="attachment.url"
              alt="Organization Document"
              class="border-round shadow-1 w-full cursor-pointer"
              style="max-height: 200px; object-fit: cover"
              @click="viewAttachment(attachment)"
            />
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div v-if="organization?.notes">
        <h4 class="text-lg font-semibold mb-3">Notes</h4>
        <div class="p-3 surface-100 border-round">
          {{ organization.notes }}
        </div>
      </div>
    </div>

    <div v-else class="flex flex-column align-items-center py-5">
      <i class="pi pi-building text-6xl text-500 mb-4"></i>
      <p class="text-xl">Organization not found</p>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Drawer from 'primevue/drawer'
import Avatar from 'primevue/avatar'

interface Organization {
  id?: string
  name?: string
  email?: string
  phone?: string
  streetAddress?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  website?: string
  industry?: string
  employeeCount?: string
  logo?: string
  notes?: string
  attachments?: Array<{
    id: string
    url: string
  }>
}

interface Props {
  visible: boolean
  organization: Organization | null
  position?: 'left' | 'right' | 'top' | 'bottom' | 'full'
  size?: string
  header?: string
  showHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  position: 'right',
  showHeader: true,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  hide: []
}>()

// Computed property for v-model
const drawerVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
})

// Computed properties
const organizationName = computed(() => {
  return props.organization?.name || 'Unknown'
})

const organizationEmail = computed(() => {
  return props.organization?.email || null
})

const organizationPhone = computed(() => {
  return props.organization?.phone || null
})

const organizationAddress = computed(() => {
  if (!props.organization) return null
  const { streetAddress, city, state, postalCode, country } = props.organization
  const parts = [streetAddress, city, state, postalCode, country]
  return parts.filter(Boolean).join(', ')
})

const drawerClass = computed(() => {
  const classes = []
  if (props.size) {
    classes.push(props.size)
  }
  return classes.join(' ')
})

const drawerStyle = computed(() => {
  if (props.position === 'full') {
    return { width: '100vw', height: '100vh' }
  }
  return {}
})

// Methods
function viewAttachment(attachment: { url: string }) {
  window.open(attachment.url, '_blank')
}
</script>

<style scoped>
.space-y-6 > * + * {
  margin-top: 1.5rem;
}
</style>
